// src/services/backoffice/explanationsService.ts

import { API_BASE_URL } from '../../config/api';

// Helper function to create FormData with session token (same pattern as other services)
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  // ✅ הוספת session token (כמו בשאר השירותים)
  const sessionToken = localStorage.getItem('backoffice_session_id');
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.keys(data).forEach(key => {
    const value = data[key];
    if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });
  return formData;
};

// Interfaces - ✅ מתואמים עם השרת
export interface Explanation {
  id: number;
  step_key: string;
  step_name: string;
  explanation_text: string;
  order_index: number;
  is_active: boolean; // ✅ שונה מ-is_deleted ל-is_active
  updated_by?: number;
  updated_by_info?: {
    name: string;
    email: string;
  } | null;
  ts: string; // ✅ שונה מ-created_at ל-ts
}

export interface ExplanationDetails extends Explanation {
  // כל השדות כבר כלולים ב-Explanation
}

export interface GetExplanationsParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean | null; // ✅ שונה מ-show_deleted ל-is_active
}

export interface GetExplanationsResponse {
  explanations: Explanation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SaveExplanationData {
  id?: number;
  step_key: string;
  step_name: string;
  content: string; // זה יהפוך ל-explanation_text בשרת
  order_index?: number;
}

// API Functions

/**
 * Get explanations list with filters and pagination
 */
export const getExplanations = async (params: GetExplanationsParams = {}): Promise<GetExplanationsResponse> => {
  try {
    const requestData = {
      page: params.page || 1,
      limit: params.limit || 25,
      ...(params.search && { search: params.search }),
      // ✅ תיקון: השרת מצפה ל-is_active ולא show_deleted
      ...(params.is_active !== null && params.is_active !== undefined && {
        is_active: params.is_active ? "1" : "0"
      })
    };

    // console.log('🔍 Fetching explanations with params:', requestData);

    const formData = createFormData(requestData);
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_explanations`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('📄 Explanations API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to fetch explanations');
    }

    // ✅ תיקון: השרת מחזיר pagination נפרד
    return {
      explanations: result.data.explanations || [],
      total: result.data.pagination?.total || 0,
      page: result.data.pagination?.page || 1,
      limit: result.data.pagination?.limit || 25,
      totalPages: result.data.pagination?.total_pages || 1
    };
  } catch (error) {
    console.error('❌ Error fetching explanations:', error);
    throw error;
  }
};

/**
 * Get explanation details by ID
 */
export const getExplanationDetails = async (id: number): Promise<ExplanationDetails> => {
  try {
    // console.log('🔍 Fetching explanation details for ID:', id);

    const formData = createFormData({ explanation_id: id }); // ✅ תיקון: explanation_id במקום id
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_explanation_details`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('📄 Explanation details API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to fetch explanation details');
    }

    return result.data.explanation;
  } catch (error) {
    console.error('❌ Error fetching explanation details:', error);
    throw error;
  }
};

/**
 * Save explanation (create if id=0, update if id>0)
 */
export const saveExplanation = async (explanationData: SaveExplanationData): Promise<Explanation> => {
  try {
    const requestData = {
      id: explanationData.id || 0,
      step_key: explanationData.step_key,
      step_name: explanationData.step_name,
      explanation_text: explanationData.content, // ✅ content → explanation_text
      ...(explanationData.order_index !== undefined && { order_index: explanationData.order_index })
    };

    // console.log('💾 Saving explanation with data:', requestData);

    const formData = createFormData(requestData);
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/save_explanation`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('💾 Save explanation API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to save explanation');
    }

    // החזרת אובייקט explanation מדומה (השרת מחזיר רק פרטים בסיסיים)
    return {
      id: result.data.explanation_id,
      step_key: result.data.step_key,
      step_name: result.data.step_name,
      explanation_text: explanationData.content,
      order_index: result.data.order_index || 0,
      is_active: true,
      ts: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error saving explanation:', error);
    throw error;
  }
};

/**
 * Soft delete explanation
 */
export const deleteExplanation = async (id: number): Promise<void> => {
  try {
    // console.log('🗑️ Deleting explanation ID:', id);

    const formData = createFormData({ explanation_id: id }); // ✅ תיקון: explanation_id במקום id
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/delete_explanation`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('🗑️ Delete explanation API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to delete explanation');
    }
  } catch (error) {
    console.error('❌ Error deleting explanation:', error);
    throw error;
  }
};

/**
 * Restore soft-deleted explanation
 */
export const restoreExplanation = async (id: number): Promise<void> => {
  try {
    // console.log('♻️ Restoring explanation ID:', id);

    const formData = createFormData({ explanation_id: id }); // ✅ תיקון: explanation_id במקום id
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/restore_explanation`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('♻️ Restore explanation API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to restore explanation');
    }
  } catch (error) {
    console.error('❌ Error restoring explanation:', error);
    throw error;
  }
};

/**
 * Reorder explanations (for future Drag & Drop)
 */
export const reorderExplanations = async (orderData: { id: number; order_index: number }[]): Promise<void> => {
  try {
    // console.log('🔄 Reordering explanations with data:', orderData);

    const requestData = {
      explanations_order: JSON.stringify(orderData) // ✅ תיקון: explanations_order במקום order_data
    };

    const formData = createFormData(requestData);
    const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/reorder_explanations`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    // console.log('🔄 Reorder explanations API Response:', result);

    if (result.res !== 'OK') {
      throw new Error(result.msg || 'Failed to reorder explanations');
    }
  } catch (error) {
    console.error('❌ Error reordering explanations:', error);
    throw error;
  }
};